import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, selectBanks } from '@/utils/store';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import CurrencyInput from '@/components/ui/CurrencyInput';
import MemberMultiSelect from '@/components/ui/MemberMultiSelect';
import { useFormDraft } from '@/hooks/useFormDraft';

export default function AddCreditModal({ open, onClose, defaultBankId, credit }) {
  const { t } = useTranslation();
  const addCredit = useStore((s) => s.addCredit);
  const updateCredit = useStore((s) => s.updateCredit);
  const banks = useStore(selectBanks);
  const isEdit = !!credit;
  const draft = useFormDraft('addCredit');

  const [bankId, setBankId] = useState(defaultBankId || '');
  const [type, setType] = useState('card_installment');
  const [description, setDescription] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [installmentAmount, setInstallmentAmount] = useState('');
  const [totalInstallments, setTotalInstallments] = useState('');
  const [paidInstallments, setPaidInstallments] = useState('0');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [memberIds, setMemberIds] = useState([]);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!bankId) errs.bankId = t('credits.form.bankRequired');
    if (!description.trim()) errs.description = t('credits.form.descriptionRequired');
    if (!totalAmount || Number(totalAmount) <= 0) errs.totalAmount = t('credits.form.amountRequired');
    if (!installmentAmount || Number(installmentAmount) <= 0) errs.installmentAmount = t('credits.form.amountRequired');
    if (!totalInstallments || Number(totalInstallments) <= 0) errs.totalInstallments = t('credits.form.installmentsRequired');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const data = {
      bankId,
      type,
      description: description.trim(),
      totalAmount: Number(totalAmount),
      installmentAmount: Number(installmentAmount),
      totalInstallments: Number(totalInstallments),
      paidInstallments: Number(paidInstallments) || 0,
      startDate,
      memberIds,
      category: 'credit',
    };
    if (isEdit) {
      updateCredit(credit.id, data);
    } else {
      addCredit(data);
    }
    draft.clear();
    handleClose();
  };

  const handleClose = () => {
    setBankId(defaultBankId || '');
    setType('card_installment');
    setDescription('');
    setTotalAmount('');
    setInstallmentAmount('');
    setTotalInstallments('');
    setPaidInstallments('0');
    setStartDate(new Date().toISOString().split('T')[0]);
    setMemberIds([]);
    setErrors({});
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    if (credit) {
      setBankId(credit.bankId);
      setType(credit.type);
      setDescription(credit.description);
      setTotalAmount(String(credit.totalAmount));
      setInstallmentAmount(String(credit.installmentAmount));
      setTotalInstallments(String(credit.totalInstallments));
      setPaidInstallments(String(credit.paidInstallments));
      setStartDate(credit.startDate);
      setMemberIds(credit.memberIds || []);
    } else {
      const saved = draft.load();
      if (saved) {
        setBankId(saved.bankId || defaultBankId || '');
        setType(saved.type || 'card_installment');
        setDescription(saved.description || '');
        setTotalAmount(saved.totalAmount || '');
        setInstallmentAmount(saved.installmentAmount || '');
        setTotalInstallments(saved.totalInstallments || '');
        setPaidInstallments(saved.paidInstallments || '0');
        setStartDate(saved.startDate || new Date().toISOString().split('T')[0]);
        setMemberIds(saved.memberIds || []);
      }
    }
    setErrors({});
  }, [credit, open]);

  // Auto-save draft
  useEffect(() => {
    if (!open || isEdit) return;
    draft.save({ bankId, type, description, totalAmount, installmentAmount, totalInstallments, paidInstallments, startDate, memberIds });
  }, [open, isEdit, bankId, type, description, totalAmount, installmentAmount, totalInstallments, paidInstallments, startDate, memberIds]);

  return (
    <Modal open={open} onClose={handleClose} title={isEdit ? t('common.edit') : t('credits.addCredit')} footer={
      <Button className="w-full" onClick={handleSave}>
        {t('common.save')}
      </Button>
    }>
      <div className="space-y-4">
        {/* Bank select */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('credits.form.bank')}
          </label>
          <div className="flex gap-2 flex-wrap">
            {banks.map((bank) => (
              <button
                key={bank.id}
                onClick={() => setBankId(bank.id)}
                className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all flex items-center gap-2 ${
                  bankId === bank.id
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-400'
                }`}
              >
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: bank.color }} />
                {bank.name}
              </button>
            ))}
          </div>
          {errors.bankId && <p className="text-xs text-danger-500 mt-1">{errors.bankId}</p>}
        </div>

        {/* Type toggle */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('credits.form.type')}
          </label>
          <div className="flex gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl">
            {['card_installment', 'consumer_loan', 'mortgage'].map((t_) => (
              <button
                key={t_}
                onClick={() => setType(t_)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                  type === t_
                    ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500'
                }`}
              >
                {t(`credits.types.${t_}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('credits.form.description')}
          </label>
          <input
            type="text"
            className={`input ${errors.description ? 'border-danger-500' : ''}`}
            placeholder={t('credits.form.descriptionPlaceholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          {errors.description && <p className="text-xs text-danger-500 mt-1">{errors.description}</p>}
        </div>

        {/* Total amount */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('credits.form.totalAmount')}
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-bold text-gray-300">$</span>
            <CurrencyInput
              placeholder="0"
              value={totalAmount}
              onChange={setTotalAmount}
              className={`w-full pl-10 pr-4 py-3 text-lg font-bold input ${errors.totalAmount ? 'border-danger-500' : ''}`}
            />
          </div>
          {errors.totalAmount && <p className="text-xs text-danger-500 mt-1">{errors.totalAmount}</p>}
        </div>

        {/* Installment amount */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('credits.form.installmentAmount')}
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-bold text-gray-300">$</span>
            <CurrencyInput
              placeholder="0"
              value={installmentAmount}
              onChange={setInstallmentAmount}
              className={`w-full pl-10 pr-4 py-3 text-lg font-bold input ${errors.installmentAmount ? 'border-danger-500' : ''}`}
            />
          </div>
          {errors.installmentAmount && <p className="text-xs text-danger-500 mt-1">{errors.installmentAmount}</p>}
        </div>

        {/* Installments row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
              {t('credits.form.totalInstallments')}
            </label>
            <input
              type="number"
              inputMode="numeric"
              placeholder="12"
              value={totalInstallments}
              onChange={(e) => setTotalInstallments(e.target.value)}
              className={`input ${errors.totalInstallments ? 'border-danger-500' : ''}`}
            />
            {errors.totalInstallments && <p className="text-xs text-danger-500 mt-1">{errors.totalInstallments}</p>}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
              {t('credits.form.paidInstallments')}
            </label>
            <input
              type="number"
              inputMode="numeric"
              placeholder="0"
              value={paidInstallments}
              onChange={(e) => setPaidInstallments(e.target.value)}
              className="input"
            />
          </div>
        </div>

        {/* Start date */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('credits.form.startDate')}
          </label>
          <input
            type="date"
            className="input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        {/* Member association */}
        <MemberMultiSelect
          value={memberIds}
          onChange={setMemberIds}
          label={t('credits.form.member')}
        />

      </div>
    </Modal>
  );
}
